import { useState, useEffect, useCallback } from 'react';
import { Amplify } from 'aws-amplify';
import { 
  signOut, 
  getCurrentUser, 
  fetchAuthSession,
  signInWithRedirect,
  AuthUser
} from 'aws-amplify/auth';
import { amplifyConfig, cognitoConfig } from '../config/cognito';

// Amplifyの初期化
Amplify.configure(amplifyConfig);

export interface CognitoAuthState {
  isAuthenticated: boolean;
  user: AuthUser | null;
  idToken: string | null;
  accessToken: string | null;
  loading: boolean;
  error: string | null;
}

export interface CognitoAuthActions {
  signInWithRedirect: () => void;
  signOutUser: () => Promise<void>;
  refreshTokens: () => Promise<void>;
  clearError: () => void;
}

export const useCognito = (): CognitoAuthState & CognitoAuthActions => {
  const [authState, setAuthState] = useState<CognitoAuthState>({
    isAuthenticated: false,
    user: null,
    idToken: null,
    accessToken: null,
    loading: true,
    error: null
  });

  // 認証状態を更新する関数（リトライ機能付き）
  const updateAuthState = useCallback(async (retryCount = 0, maxRetries = 5) => {
    try {
      setAuthState(prev => ({ ...prev, loading: true, error: null }));

      console.log(`[DEBUG] Checking auth state (attempt ${retryCount + 1}/${maxRetries + 1})`);

      // セッション情報を確認
      const session = await fetchAuthSession({ forceRefresh: true });
      
      const idToken = session.tokens?.idToken?.toString() || null;
      const accessToken = session.tokens?.accessToken?.toString() || null;

      console.log('[DEBUG] Session check:', {
        hasTokens: !!session.tokens,
        hasIdToken: !!idToken,
        hasAccessToken: !!accessToken,
        attempt: retryCount + 1
      });

      if (idToken) {
        // セッションが有効な場合のみユーザー情報を取得
        try {
          const user = await getCurrentUser();
          console.log('[DEBUG] Authentication successful:', {
            userId: user.userId,
            username: user.username,
            hasIdToken: !!idToken,
            hasAccessToken: !!accessToken
          });

          setAuthState({
            isAuthenticated: true,
            user,
            idToken,
            accessToken,
            loading: false,
            error: null
          });
          return true; // 成功
        } catch (userError) {
          console.log('[DEBUG] User info error, but session exists:', userError);
          // セッションはあるがユーザー情報取得に失敗した場合も認証済みとする
          setAuthState({
            isAuthenticated: true,
            user: { username: 'authenticated_user', userId: 'unknown' } as AuthUser,
            idToken,
            accessToken,
            loading: false,
            error: null
          });
          return true; // 成功
        }
      } else if (retryCount < maxRetries) {
        // トークンがない場合、リトライ
        console.log(`[DEBUG] No tokens found, retrying in ${(retryCount + 1) * 1000}ms...`);
        await new Promise(resolve => setTimeout(resolve, (retryCount + 1) * 1000));
        return await updateAuthState(retryCount + 1, maxRetries);
      } else {
        console.log('[DEBUG] No valid session found after all retries');
        setAuthState({
          isAuthenticated: false,
          user: null,
          idToken: null,
          accessToken: null,
          loading: false,
          error: null
        });
        return false; // 失敗
      }
    } catch (error) {
      console.log(`[DEBUG] Session check error (attempt ${retryCount + 1}):`, error);
      
      if (retryCount < maxRetries) {
        console.log(`[DEBUG] Retrying auth check in ${(retryCount + 1) * 1000}ms...`);
        await new Promise(resolve => setTimeout(resolve, (retryCount + 1) * 1000));
        return await updateAuthState(retryCount + 1, maxRetries);
      } else {
        setAuthState({
          isAuthenticated: false,
          user: null,
          idToken: null,
          accessToken: null,
          loading: false,
          error: null
        });
        return false; // 失敗
      }
    }
  }, []);

  // 初期化時に認証状態をチェック
  useEffect(() => {
    updateAuthState();
  }, [updateAuthState]);

  // URLのコールバック処理（認証後のリダイレクト処理）
  useEffect(() => {
    const handleAuthCallback = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get('code');
      const error = urlParams.get('error');
      
      if (error) {
        console.error('[DEBUG] OAuth error:', error);
        setAuthState(prev => ({ 
          ...prev, 
          error: `認証エラー: ${error}`,
          loading: false 
        }));
        return;
      }
      
      if (code) {
        console.log('[DEBUG] Processing auth code:', code);
        
        // Amplifyが内部でトークン交換を処理するまで待機
        let attempts = 0;
        const maxAttempts = 10;
        const checkInterval = 1000; // 1秒間隔
        
        const checkAuthState = async () => {
          attempts++;
          console.log(`[DEBUG] Checking auth state (attempt ${attempts}/${maxAttempts})`);
          
          try {
            // forceRefreshでセッションを強制更新
            const session = await fetchAuthSession({ forceRefresh: true });
            
            if (session.tokens?.idToken) {
              console.log('[DEBUG] Tokens found, updating auth state');
              await updateAuthState();
              
              // URLからコードを削除
              const newUrl = window.location.pathname;
              window.history.replaceState({}, document.title, newUrl);
              
              console.log('[DEBUG] Auth callback processed successfully');
              return;
            }
          } catch (sessionError) {
            console.log(`[DEBUG] Session check failed (attempt ${attempts}):`, sessionError);
          }
          
          if (attempts < maxAttempts) {
            setTimeout(checkAuthState, checkInterval);
          } else {
            console.log('[DEBUG] Auth callback failed after all attempts');
            setAuthState(prev => ({ 
              ...prev, 
              error: '認証処理がタイムアウトしました。再度お試しください。',
              loading: false 
            }));
          }
        };
        
        // 最初の待機後にチェック開始
        setTimeout(checkAuthState, 2000);
      }
    };

    handleAuthCallback();
  }, [updateAuthState]);

  // サインイン（リダイレクト方式）
  const signInWithRedirectHandler = useCallback(async () => {
    try {
      setAuthState(prev => ({ ...prev, loading: true, error: null }));
      
      console.log('[DEBUG] Starting sign in with redirect...');
      
      // Amplify v6のsignInWithRedirectを使用（providerパラメータなし）
      await signInWithRedirect();
      
    } catch (error) {
      console.error('[DEBUG] Sign in error:', error);
      setAuthState(prev => ({ 
        ...prev, 
        error: 'サインインに失敗しました',
        loading: false 
      }));
    }
  }, []);

  // サインアウト
  const signOutUser = useCallback(async () => {
    try {
      setAuthState(prev => ({ ...prev, loading: true, error: null }));
      
      console.log('[DEBUG] Signing out...');
      
      await signOut();
      
      setAuthState({
        isAuthenticated: false,
        user: null,
        idToken: null,
        accessToken: null,
        loading: false,
        error: null
      });
      
      console.log('[DEBUG] Sign out successful');
    } catch (error) {
      console.error('[DEBUG] Sign out error:', error);
      setAuthState(prev => ({ 
        ...prev, 
        error: 'サインアウトに失敗しました',
        loading: false 
      }));
    }
  }, []);

  // トークンの更新
  const refreshTokens = useCallback(async () => {
    console.log('[DEBUG] Refreshing tokens...');
    await updateAuthState();
  }, [updateAuthState]);

  // エラーのクリア
  const clearError = useCallback(() => {
    setAuthState(prev => ({ ...prev, error: null }));
  }, []);

  return {
    ...authState,
    signInWithRedirect: signInWithRedirectHandler,
    signOutUser,
    refreshTokens,
    clearError
  };
};
